import React from 'react';
import HelpBadge from './components/HelpBadge';

type Source = {
  id: string;
  kind: string;
  kindLabel?: string;
  delivery?: string;
  kindSummary?: string;
  label: string;
  address?: string;
  detail?: string;
  platforms?: string[];
  enabled: boolean;
};

type Kind = {
  id: string;
  label: string;
  delivery: string;
  summary: string;
};

type SocialTriage = {
  kind?: string;
  is_services_lead?: boolean;
  role?: string;
  company?: string;
  category?: string;
  location?: string;
  project?: string;
  timeline?: string;
  priority?: string;
  operator_actions?: string[];
};

type SocialMessage = {
  id: number;
  platform: string;
  from: string;
  subject: string;
  snippet: string;
  mailbox: string;
  date: string;
  status: string;
  created_at: string;
  intent?: string;
  triage?: SocialTriage;
};

type Overview = {
  company_id: number;
  company_name: string;
  kinds?: Kind[];
  sources?: Source[];
  daily_enabled?: boolean;
  last_run_at?: string | null;
  last_summary?: { scanned?: number; created?: number; platforms?: Record<string, number> } | null;
  gmail_configured?: boolean;
  messages?: SocialMessage[];
};

type Props = {
  gatewayBaseUrl: string;
  accessToken: string;
  companyId: number | null;
  projectId?: number | null;
  language?: 'en' | 'es';
};

const platformLabel = (platform: string, spanish: boolean): string => {
  const map: Record<string, [string, string]> = {
    linkedin: ['LinkedIn', 'LinkedIn'],
    meta: ['Meta', 'Meta'],
    x: ['X', 'X'],
    threads: ['Threads', 'Threads'],
    other: ['Other', 'Otro'],
  };
  const row = map[platform] || map.other;
  return spanish ? row[1] : row[0];
};

const RazzmatazzSocialIntakePanel: React.FC<Props> = ({
  gatewayBaseUrl,
  accessToken,
  companyId,
  projectId = null,
  language = 'en',
}) => {
  const spanish = language === 'es';
  const [data, setData] = React.useState<Overview | null>(null);
  const [checked, setChecked] = React.useState<Set<string>>(new Set());
  const [dailyOn, setDailyOn] = React.useState(true);
  const [error, setError] = React.useState('');
  const [note, setNote] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  const headers = React.useMemo(
    () => ({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    }),
    [accessToken],
  );

  const load = React.useCallback(async () => {
    if (!companyId) {
      setData(null);
      return;
    }
    setBusy(true);
    setError('');
    try {
      const response = await fetch(
        `${gatewayBaseUrl}/api/v1/razzmatazz/social-intake?company_id=${companyId}`,
        { headers },
      );
      const payload = (await response.json().catch(() => ({}))) as Overview & { detail?: string };
      if (!response.ok) {
        throw new Error(typeof payload.detail === 'string' ? payload.detail : `Intake returned ${response.status}`);
      }
      setData(payload);
      setChecked(new Set((payload.sources || []).filter((row) => row.enabled).map((row) => row.id)));
      setDailyOn(payload.daily_enabled !== false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [companyId, gatewayBaseUrl, headers]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const toggle = (id: string) => {
    setChecked((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const save = async () => {
    if (!companyId) return;
    setBusy(true);
    setError('');
    setNote('');
    try {
      const response = await fetch(`${gatewayBaseUrl}/api/v1/razzmatazz/social-intake`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          company_id: companyId,
          sourceIds: [...checked],
          dailyEnabled: dailyOn,
        }),
      });
      const payload = (await response.json().catch(() => ({}))) as Overview & { detail?: string };
      if (!response.ok) {
        throw new Error(typeof payload.detail === 'string' ? payload.detail : 'Could not save intake sources.');
      }
      setData((current) => ({ ...(current || payload), ...payload, messages: current?.messages || payload.messages }));
      setNote(spanish ? 'Fuentes de entrada guardadas.' : 'Saved intake sources.');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (messageId: number, status: string) => {
    if (!companyId) return;
    setBusy(true);
    setError('');
    setNote('');
    try {
      const response = await fetch(
        `${gatewayBaseUrl}/api/v1/razzmatazz/social-intake/messages/${messageId}/status`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ company_id: companyId, status }),
        },
      );
      const payload = (await response.json().catch(() => ({}))) as { detail?: string };
      if (!response.ok) {
        throw new Error(typeof payload.detail === 'string' ? payload.detail : 'Status update failed.');
      }
      setNote(spanish ? `Estado → ${status}` : `Status → ${status}`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const pushPursuit = async (messageId: number) => {
    if (!companyId) return;
    setBusy(true);
    setError('');
    setNote('');
    try {
      const response = await fetch(
        `${gatewayBaseUrl}/api/v1/razzmatazz/social-intake/messages/${messageId}/pursuit`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ company_id: companyId }),
        },
      );
      const payload = (await response.json().catch(() => ({}))) as {
        detail?: string;
        note?: string;
        placeholder_email?: string;
      };
      if (!response.ok) {
        throw new Error(typeof payload.detail === 'string' ? payload.detail : 'Pursuit push failed.');
      }
      setNote(
        [
          spanish ? 'Enviado a Pursuit.' : 'Pushed to Pursuit.',
          payload.placeholder_email ? `Email placeholder: ${payload.placeholder_email}` : '',
          payload.note || '',
        ].filter(Boolean).join(' '),
      );
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const reviewNow = async () => {
    if (!companyId) return;
    setBusy(true);
    setError('');
    setNote('');
    try {
      const saveResponse = await fetch(`${gatewayBaseUrl}/api/v1/razzmatazz/social-intake`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          company_id: companyId,
          sourceIds: [...checked],
          dailyEnabled: dailyOn,
        }),
      });
      const savePayload = (await saveResponse.json().catch(() => ({}))) as { detail?: string };
      if (!saveResponse.ok) {
        throw new Error(typeof savePayload.detail === 'string' ? savePayload.detail : 'Could not save intake sources.');
      }
      const response = await fetch(`${gatewayBaseUrl}/api/v1/razzmatazz/social-intake/run`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ company_id: companyId, project_id: projectId, force: true }),
      });
      const payload = (await response.json().catch(() => ({}))) as {
        detail?: string;
        skipped?: boolean;
        reason?: string;
        summary?: { scanned?: number; created?: number };
      };
      if (!response.ok) {
        throw new Error(typeof payload.detail === 'string' ? payload.detail : 'Review failed.');
      }
      if (payload.skipped) {
        setNote(payload.reason || (spanish ? 'Ya revisado hoy.' : 'Already reviewed today.'));
      } else {
        const scanned = payload.summary?.scanned ?? 0;
        const created = payload.summary?.created ?? 0;
        setNote(
          spanish
            ? `Revisados ${scanned}; ${created} nuevos.`
            : `Scanned ${scanned}; ${created} new.`,
        );
      }
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  if (!companyId) {
    return (
      <p className="razz-empty" role="status">
        {spanish
          ? 'Elige un proyecto para configurar el correo social.'
          : 'Pick a project to configure social mail intake.'}
      </p>
    );
  }

  const sources = data?.sources || [];
  const kinds = data?.kinds || [];
  const byKind = new Map<string, Source[]>();
  for (const source of sources) {
    const key = source.kind || 'mailbox';
    if (!byKind.has(key)) byKind.set(key, []);
    byKind.get(key)!.push(source);
  }

  return (
    <div className="razz-social-intake">
      <header className="razz-card">
        <p className="razz-kicker">Razzmatazz</p>
        <h3>
          {spanish ? 'Correo social' : 'Social mail'}{' '}
          <HelpBadge label={spanish ? 'Qué es correo social' : 'What is social mail'}>
            <p>
              {spanish
                ? 'Bandejas de notificaciones de LinkedIn, Meta, X y Threads. Elige cuáles revisa Razzmatazz — igual que las fuentes de entrada en CompliDesk.'
                : 'LinkedIn, Meta, X, and Threads notification mailboxes. Pick which ones Razzmatazz reviews — same intake checkboxes as CompliDesk.'}
            </p>
          </HelpBadge>
        </h3>
        <p className="razz-muted">
          {spanish
            ? 'Estas alias ya no van al service desk. Márcalas aquí para que Razzmatazz las lea.'
            : 'These aliases no longer go to the service desk. Check them here so Razzmatazz reads them.'}
        </p>
      </header>

      {error ? <p className="razz-error" role="alert">{error}</p> : null}
      {note ? <p className="razz-muted" role="status">{note}</p> : null}

      {!sources.length ? (
        <p className="razz-empty" role="status">
          {spanish
            ? 'No hay buzones sociales definidos para esta cuenta.'
            : 'No social mailboxes are defined for this account.'}
        </p>
      ) : (
        <form
          className="razz-card razz-intake-form"
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <div className="razz-intake-source-list" aria-label={spanish ? 'Fuentes de entrada' : 'Intake sources'}>
            {[...byKind.entries()].map(([kind, rows]) => {
              const kindMeta = kinds.find((item) => item.id === kind);
              const delivery = kindMeta?.delivery || rows[0]?.delivery || 'pull';
              return (
                <section key={kind} className="razz-intake-kind">
                  <header className="razz-intake-kind-head">
                    <h4>{kindMeta?.label || rows[0]?.kindLabel || kind}</h4>
                    <span className={`razz-intake-delivery razz-intake-delivery-${delivery}`}>{delivery}</span>
                  </header>
                  {(kindMeta?.summary || rows[0]?.kindSummary) ? (
                    <p className="razz-muted">{kindMeta?.summary || rows[0]?.kindSummary}</p>
                  ) : null}
                  <ul className="razz-intake-source-items">
                    {rows.map((source) => (
                      <li key={source.id}>
                        <label className="razz-intake-source-row">
                          <input
                            type="checkbox"
                            checked={checked.has(source.id)}
                            onChange={() => toggle(source.id)}
                            disabled={busy}
                          />
                          <span className="razz-intake-source-copy">
                            <strong>{source.label || source.address || source.id}</strong>
                            <span className="razz-muted">
                              {[source.address, source.detail].filter(Boolean).join(' — ')}
                            </span>
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}
          </div>

          <div className="razz-intake-schedule">
            <div>
              <h4>{spanish ? 'Revisión diaria' : 'Daily review'}</h4>
              <p className="razz-muted">
                {spanish
                  ? 'Una vez al día, Razzmatazz lee las bandejas marcadas. No responde.'
                  : 'Once a day, Razzmatazz reads the checked mailboxes. It does not reply.'}
              </p>
            </div>
            <label className="razz-intake-toggle">
              <input
                type="checkbox"
                checked={dailyOn}
                onChange={(event) => setDailyOn(event.target.checked)}
                disabled={busy}
              />
              <span>{spanish ? 'Activar revisión diaria' : 'Turn on daily review'}</span>
            </label>
          </div>

          <div className="razz-intake-actions">
            <button type="submit" className="dashboard-btn" disabled={busy}>
              {spanish ? 'Guardar fuentes' : 'Save intake sources'}
            </button>
            <button type="button" className="dashboard-btn" disabled={busy} onClick={() => void reviewNow()}>
              {spanish ? 'Revisar ahora' : 'Review now'}
            </button>
          </div>

          {data?.last_run_at || data?.last_summary ? (
            <p className="razz-muted">
              {data.last_run_at
                ? (spanish ? `Última revisión: ${data.last_run_at}` : `Last review: ${data.last_run_at}`)
                : null}
              {data.last_summary?.scanned != null
                ? ` · ${spanish ? 'Escaneados' : 'Scanned'} ${data.last_summary.scanned}`
                : null}
              {data.gmail_configured === false
                ? (spanish
                  ? ' · Gmail aún no está configurado en este entorno.'
                  : ' · Gmail is not configured in this environment yet.')
                : null}
            </p>
          ) : null}
        </form>
      )}

      <section className="razz-card" aria-labelledby="razz-social-inbox-heading">
        <h3 id="razz-social-inbox-heading">{spanish ? 'Bandeja reciente' : 'Recent inbox'}</h3>
        {!(data?.messages || []).length ? (
          <p className="razz-muted">
            {spanish
              ? 'Aún no hay mensajes. Guarda las fuentes y pulsa Revisar ahora.'
              : 'No messages yet. Save intake sources and press Review now.'}
          </p>
        ) : (
          <ul className="razz-social-message-list">
            {(data?.messages || []).map((message) => {
              const triage = message.triage;
              const lead = Boolean(triage?.is_services_lead);
              return (
                <li key={message.id} className={`razz-social-message${lead ? ' is-lead' : ''}`}>
                  <span className="razz-social-platform">{platformLabel(message.platform, spanish)}</span>
                  <div>
                    <strong>{message.subject || '(no subject)'}</strong>
                    <p className="razz-muted">
                      {message.from}
                      {message.status ? ` · ${message.status}` : ''}
                      {triage?.priority === 'high' ? (spanish ? ' · prioridad alta' : ' · high priority') : ''}
                    </p>
                    {lead ? (
                      <div className="razz-social-triage">
                        <p>
                          {[triage?.role, triage?.company && `at ${triage.company}`, triage?.category && `· ${triage.category}`]
                            .filter(Boolean)
                            .join(' ')}
                        </p>
                        {triage?.location ? <p className="razz-muted">{triage.location}</p> : null}
                        {triage?.project ? <p>{triage.project}</p> : null}
                        {triage?.timeline ? (
                          <p className="razz-muted">
                            {spanish ? 'Plazo: ' : 'Timeline: '}
                            {triage.timeline}
                          </p>
                        ) : null}
                        <ol className="razz-muted">
                          {(triage?.operator_actions || []).slice(0, 3).map((step) => (
                            <li key={step}>{step}</li>
                          ))}
                        </ol>
                        <div className="razz-intake-actions">
                          <button
                            type="button"
                            className="dashboard-btn"
                            disabled={busy}
                            onClick={() => void pushPursuit(message.id)}
                          >
                            {spanish ? 'Enviar a Pursuit' : 'Push to Pursuit'}
                          </button>
                          <button
                            type="button"
                            className="dashboard-btn"
                            disabled={busy}
                            onClick={() => void setStatus(message.id, 'proposed')}
                          >
                            {spanish ? 'Propuesta enviada' : 'Marked proposed'}
                          </button>
                          <button
                            type="button"
                            className="dashboard-btn"
                            disabled={busy}
                            onClick={() => void setStatus(message.id, 'declined')}
                          >
                            {spanish ? 'Rechazado' : 'Declined'}
                          </button>
                          <a
                            className="dashboard-btn"
                            href="https://www.linkedin.com/services/"
                            target="_blank"
                            rel="noreferrer"
                          >
                            {spanish ? 'Abrir LinkedIn Services' : 'Open LinkedIn Services'}
                          </a>
                        </div>
                      </div>
                    ) : (
                      message.snippet ? <p className="razz-muted">{message.snippet}</p> : null
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
};

export default RazzmatazzSocialIntakePanel;
